<template>
  <section class="positions">
    <div class="head">
      <strong>Our positions</strong>
      <button type="button" :disabled="loading || !mint" title="Refresh" @click="load">↻</button>
    </div>

    <div v-if="loading" class="empty">Loading...</div>
    <div v-else-if="error" class="error">{{ error }}</div>

    <div v-else class="positions-body">
      <div v-if="incompleteCashFlow" class="warning">Cash flow unresolved — PnL excludes the unknown proceeds.</div>
      <div v-if="positionRows.length > 0" class="wallets">
        <div v-for="row in positionRows" :key="row.owner" class="wallet-row">
          <div>
            <b>{{ row.name }}</b>
            <span>{{ shortKey(row.owner, 5, 5, 16) }}<em v-if="row.stage">{{ row.stage }}</em></span>
          </div>
          <span>{{ formatAmountHuman(row.tokens) }}</span>
          <strong :class="{ pos: row.pnl > 0, neg: row.pnl < 0 }">{{ pnlText(row.pnl, row.basis) }}</strong>
        </div>
      </div>

      <div v-if="tradeRows.length > 0" class="trades">
        <button v-for="trade in tradeRows" :key="tradeKey(trade)" type="button" class="trade" :class="trade.method" @click="openTx(trade.signature)">
          <span class="side">{{ trade.method }}</span>
          <span class="amount">{{ formatAmountHuman(trade.amount) }}</span>
          <span class="sol">{{ formatSol(trade.valueLamports) }}</span>
          <span v-if="trade.pnl != null" class="pnl" :class="{ pos: trade.pnl > 0, neg: trade.pnl < 0 }">{{ pnlText(trade.pnl, trade.pnlBasis) }}</span>
          <span class="sig">{{ shortKey(trade.signature, 6, 4, 14) }}</span>
        </button>
      </div>
      <div v-else class="empty">No our trades</div>
    </div>
  </section>
</template>

<script setup>
import { computed, onMounted, ref, watch } from 'vue'
import { useStorage } from '@/stores'
import { fetchJson } from '@/stores/api/http'
import { formatAmountHuman, formatSol, shortKey } from '@/composables/format'
import { formatPnlWithPct } from '@/components/_shared/format'

const props = defineProps({
  entity: { type: Object, default: null },
  positionPnl: { type: Object, default: null },
})

const store = useStorage()
const loading = ref(false)
const error = ref('')
const trades = ref([])

const mint = computed(() => String(props.entity?.mint || '').trim())
const wallets = computed(() => Object.values(store.wallets || {}))
const walletMap = computed(() => {
  const out = new Map()
  for (const wallet of wallets.value) {
    const pk = String(wallet?.public_key || wallet?.publicKey || '').trim()
    if (pk) out.set(pk, wallet)
  }
  return out
})
const price = computed(() => {
  const amount = num(props.entity?.amount)
  const lamports = num(props.entity?.lamports)
  return amount > 0 && lamports > 0 ? lamports / amount : 0
})

const calc = computed(() => calculate(trades.value))
const positionRows = computed(() => {
  if (positionClosed.value) return []
  return [...calc.value.positions.values()]
    .filter(row => row.tokens > 0)
    .map(row => ({
      ...row,
      name: walletMap.value.get(row.owner)?.name || 'Wallet',
      stage: positionStage.value,
      pnl: Math.round(row.realized + row.tokens * price.value - row.cost),
      basis: row.spent,
    }))
    .sort((a, b) => Math.abs(b.pnl) - Math.abs(a.pnl))
})
const tradeRows = computed(() => calc.value.trades.slice(0, 18))
const positionStage = computed(() => String(props.positionPnl?.stage || '').trim().toUpperCase())
const incompleteCashFlow = computed(() => Boolean(props.positionPnl?.incomplete_cash_flow ?? props.positionPnl?.incompleteCashFlow))
const positionClosed = computed(() => {
  if (!props.positionPnl || typeof props.positionPnl !== 'object') return false
  if (positionStage.value === 'FINAL') return true
  const tokens = Number(props.positionPnl?.token_amount ?? props.positionPnl?.tokenAmount ?? 0)
  return Number.isFinite(tokens) && tokens <= 0 && !!props.positionPnl?.closed_at
})

onMounted(load)
watch(mint, () => load())

async function load() {
  if (!mint.value) return
  loading.value = true
  error.value = ''
  try {
    const qs = new URLSearchParams({ mint: mint.value, page: '1' })
    const res = await fetchJson(`/api/coin/trades?${qs.toString()}`)
    trades.value = (Array.isArray(res.trades) ? res.trades : []).map(trade => ({ ...trade, mint: trade.mint || mint.value }))
  } catch (err) {
    error.value = err?.message || 'Trades failed'
  } finally {
    loading.value = false
  }
}

function calculate(rows) {
  const positions = new Map()
  const output = []
  const our = walletMap.value

  for (const trade of (Array.isArray(rows) ? rows : []).slice().reverse()) {
    const owner = String(trade?.owner || '').trim()
    if (!our.has(owner)) continue

    const method = String(trade?.method || '').trim()
    if (String(trade?.source || '').trim() === 'reconciliation') continue
    const amount = num(trade?.amount)
    const value = Math.abs(signedLamports(trade) || num(trade?.lamports))
    const pos = positions.get(owner) || { owner, tokens: 0, cost: 0, spent: 0, realized: 0 }
    let pnl = null
    let pnlBasis = 0

    if ((method === 'buy' || method === 'add') && amount > 0) {
      pos.tokens += amount
      pos.cost += value
      pos.spent += value
    } else if (method === 'sell' && amount > 0) {
      if (pos.tokens > 0 && pos.cost > 0) {
        const sold = Math.min(amount, pos.tokens)
        const cost = pos.cost * sold / pos.tokens
        pnlBasis = cost
        pnl = Math.round(value - cost)
        pos.realized += pnl
        pos.tokens = Math.max(0, pos.tokens - sold)
        pos.cost = Math.max(0, pos.cost - cost)
      }
    }

    positions.set(owner, pos)
    output.push({ ...trade, method, valueLamports: value, pnl, pnlBasis })
  }

  return { positions, trades: output.reverse() }
}

function signedLamports(trade) {
  const net = Number(trade?.net_lamports ?? trade?.netLamports ?? 0)
  if (Number.isFinite(net) && net !== 0) return net
  const lamports = num(trade?.lamports)
  return trade?.method === 'sell' ? lamports : -lamports
}

function signedSol(lamports) {
  const n = Number(lamports || 0)
  const sign = n > 0 ? '+' : n < 0 ? '-' : ''
  return `${sign}${formatSol(Math.abs(n))}`
}

function pnlText(lamports, basisLamports) {
  return formatPnlWithPct(lamports, basisLamports)
}

function tradeKey(trade) {
  return `${trade?.signature || ''}:${trade?.method || ''}:${trade?.owner || ''}:${trade?.created_at || ''}`
}

function openTx(signature) {
  const sig = String(signature || '').trim()
  if (!sig) return
  window.open(`https://solscan.io/tx/${encodeURIComponent(sig)}`, '_blank', 'noopener,noreferrer')
}

function num(value) {
  const n = Number(value || 0)
  return Number.isFinite(n) ? n : 0
}
</script>

<style scoped>
.positions{
  border-top:1px solid #222;
  padding:10px 12px 12px;
  display:grid;
  grid-template-rows:auto minmax(0, 1fr);
  gap:8px;
  height:142px;
  min-height:142px;
}
.head{
  display:flex;
  align-items:center;
  justify-content:space-between;
  gap:8px;
}
.head strong{ color:#fff; font-size:13px; }
.head button{
  width:24px;
  height:24px;
  border:1px solid #333;
  border-radius:999px;
  background:#111;
  color:#94a3b8;
  cursor:pointer;
}
.positions-body{
  min-height:0;
  overflow-y:auto;
  display:grid;
  gap:6px;
  scrollbar-width:thin;
  scrollbar-color:#333 #111;
}
.positions-body::-webkit-scrollbar{ width:6px; }
.positions-body::-webkit-scrollbar-track{ background:#111; }
.positions-body::-webkit-scrollbar-thumb{ background:#333; border-radius:999px; }
.wallets,.trades{ display:grid; gap:6px; }
.wallet-row,
.trade{
  min-width:0;
  border:1px solid #242424;
  border-radius:7px;
  background:#0f0f0f;
  color:#e5e7eb;
}
.wallet-row{
  min-height:40px;
  display:grid;
  grid-template-columns:minmax(0, 1fr) auto auto;
  align-items:center;
  gap:10px;
  padding:7px 9px;
}
.wallet-row div{ min-width:0; display:grid; gap:2px; }
.wallet-row b,
.wallet-row span{
  min-width:0;
  overflow:hidden;
  text-overflow:ellipsis;
  white-space:nowrap;
}
.wallet-row b{ color:#fff; font-size:12px; }
.wallet-row span{ color:#8fa1b5; font-size:11px; }
.wallet-row em{
  display:inline-flex;
  margin-left:6px;
  padding:1px 5px;
  border:1px solid #334155;
  border-radius:999px;
  color:#c4b5fd;
  font-size:9px;
  font-style:normal;
  font-weight:900;
  line-height:1;
  white-space:nowrap;
}
.wallet-row > span{ color:#cbd5e1; font-weight:800; }
.wallet-row strong{ font-size:12px; font-variant-numeric:tabular-nums; }
.trade{
  min-height:34px;
  display:grid;
  grid-template-columns:38px minmax(0, 1fr) auto auto auto;
  align-items:center;
  gap:8px;
  padding:6px 8px;
  text-align:left;
  cursor:pointer;
}
.trade:hover{ border-color:#343434; background:#121212; }
.side{ font-size:11px; font-weight:900; }
.trade.buy .side{ color:#86efac; }
.trade.sell .side{ color:#fca5a5; }
.amount{ color:#cbd5e1; font-size:12px; font-weight:800; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.sol,.pnl{ font-size:12px; font-weight:900; font-variant-numeric:tabular-nums; white-space:nowrap; }
.sol{ color:#f8fafc; }
.sig{ color:#93c5fd; font-size:11px; font-weight:700; white-space:nowrap; }
.pos{ color:#22c55e; }
.neg{ color:#f87171; }
.empty,.error{
  padding:10px;
  text-align:center;
  color:#64748b;
  font-size:12px;
}
.error{ color:#fca5a5; }
.warning{
  padding:7px 9px;
  border:1px solid #854d0e;
  border-radius:7px;
  color:#facc15;
  background:#1c1608;
  font-size:11px;
}
@media (max-width: 640px){
  .trade{ grid-template-columns:38px minmax(0, 1fr) auto auto; }
  .sig{ display:none; }
}
</style>
